import clickSound from "@/assets/audio/ui-click.mp3";
import operationErrorSound from "@/assets/audio/ui-operation-error.mp3";
import paymentSuccessSound from "@/assets/audio/ui-payment-success.mp3";
import rewardSaleSound from "@/assets/audio/ui-reward-sale.mp3";
import storePurchaseSound from "@/assets/audio/ui-store-purchase.mp3";

const SOUND_SOURCES = {
  click: clickSound,
  error: operationErrorSound,
  payment: paymentSuccessSound,
  reward: rewardSaleSound,
  sale: rewardSaleSound,
  purchase: storePurchaseSound,
};

const CLICK_SELECTOR = [
  "button:not([disabled])",
  "a[href]",
  "input[type='button']:not([disabled])",
  "input[type='submit']:not([disabled])",
  "[role='button']:not([aria-disabled='true'])",
  "[data-pk-sound-click]",
].join(",");

let clickSoundInstalled = false;
let feedbackSoundInstalled = false;
let soundUnlockInstalled = false;
let unlockPlayer = null;
const soundPlayers = new Map();
const activeSounds = new Set();

function soundVolume() {
  if (localStorage.getItem("pizzaking-sound-enabled") === "0") {
    return 0;
  }
  return Math.min(1, Math.max(0, Number(localStorage.getItem("pizzaking-sound-volume") || 50) / 100));
}

function createPlayer(source) {
  const audio = new Audio(source);
  audio.preload = "auto";
  audio.load();
  return audio;
}

function basePlayer(name) {
  const soundName = SOUND_SOURCES[name] ? name : "click";
  if (!soundPlayers.has(soundName)) {
    soundPlayers.set(soundName, createPlayer(SOUND_SOURCES[soundName]));
  }
  return soundPlayers.get(soundName);
}

function releasePlayer(audio) {
  activeSounds.delete(audio);
}

function unlockAudioElement(audio) {
  if (audio.dataset.pkSoundUnlocked === "1" || audio.dataset.pkSoundUnlocking === "1") {
    return;
  }
  audio.dataset.pkSoundUnlocking = "1";
  const muted = audio.muted;
  const volume = audio.volume;
  audio.muted = true;
  audio.volume = 0;
  const playback = audio.play();
  if (!playback || typeof playback.then !== "function") {
    audio.pause();
    audio.currentTime = 0;
    audio.muted = muted;
    audio.volume = volume;
    audio.dataset.pkSoundUnlocked = "1";
    delete audio.dataset.pkSoundUnlocking;
    return;
  }
  playback.then(() => {
    audio.pause();
    audio.currentTime = 0;
    audio.muted = muted;
    audio.volume = volume;
    audio.dataset.pkSoundUnlocked = "1";
    delete audio.dataset.pkSoundUnlocking;
  }).catch(() => {
    audio.muted = muted;
    audio.volume = volume;
    delete audio.dataset.pkSoundUnlocking;
  });
}

function unlockSoundPlayback() {
  if (!soundVolume()) {
    return;
  }
  if (!unlockPlayer) {
    unlockPlayer = createPlayer(SOUND_SOURCES.click);
  }
  unlockAudioElement(unlockPlayer);
  document.querySelectorAll("audio").forEach(unlockAudioElement);
}

function installSoundUnlock() {
  if (soundUnlockInstalled) {
    return;
  }
  soundUnlockInstalled = true;
  Object.keys(SOUND_SOURCES).forEach(basePlayer);
  document.addEventListener("pointerdown", unlockSoundPlayback, { capture: true, passive: true });
  document.addEventListener("touchstart", unlockSoundPlayback, { capture: true, passive: true });
  document.addEventListener("keydown", unlockSoundPlayback, { capture: true });
}

export function playUiSound(name) {
  const volume = soundVolume();
  if (!volume) {
    return;
  }
  const base = basePlayer(name);
  const audio = base.paused || base.ended ? base : base.cloneNode(true);
  activeSounds.add(audio);
  audio.preload = "auto";
  audio.muted = false;
  audio.volume = volume;
  audio.currentTime = 0;
  audio.addEventListener("ended", () => releasePlayer(audio), { once: true });
  audio.addEventListener("error", () => releasePlayer(audio), { once: true });
  const playback = audio.play();
  if (playback && typeof playback.catch === "function") {
    playback.catch(() => releasePlayer(audio));
  }
}

export function installUiClickSound() {
  if (clickSoundInstalled) {
    return;
  }
  clickSoundInstalled = true;
  installSoundUnlock();
  document.addEventListener("click", (event) => {
    let target = event.target instanceof Element ? event.target.closest(CLICK_SELECTOR) : null;
    let cursorTarget = event.target instanceof Element ? event.target : null;
    while (!target && cursorTarget && cursorTarget !== document.body) {
      if (window.getComputedStyle(cursorTarget).cursor === "pointer") {
        target = cursorTarget;
      }
      cursorTarget = cursorTarget.parentElement;
    }
    if (target) {
      playUiSound("click");
    }
  });
}

export function installUiFeedbackSound() {
  if (feedbackSoundInstalled) {
    return;
  }
  feedbackSoundInstalled = true;
  const feedbackSelector = ".el-message--error, .el-message--warning";
  const observer = new MutationObserver((entries) => {
    entries.forEach((entry) => {
      entry.addedNodes.forEach((node) => {
        if (node.nodeType === 1 && (node.matches(feedbackSelector) || node.querySelector(feedbackSelector))) {
          playUiSound("error");
        }
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}
